import React from 'react';
import { Shield, Users, Code,  Star, Globe, Heart } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1c3d6e] via-[#1e3a8a] to-[#334E7D] backdrop-blur-sm"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-24">
          <div className="flex flex-col items-center text-center space-y-8">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-white">Trusted by 10,000+ developers worldwide</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight max-w-4xl">
              Crafting the Future of
              <span className="block mt-2 bg-gradient-to-r from-blue-200 to-white bg-clip-text text-transparent">
                Web Development
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-2xl">
              At TemplateHub, we're revolutionizing how developers and businesses create stunning websites through our expertly crafted templates and tools.
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-white/5 rounded-2xl backdrop-blur-xl"></div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=600&fit=crop"
                alt="Our Mission"
                className="rounded-xl shadow-2xl border border-white/10"
              />
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#1c3d6e] to-[#334E7D] px-8 py-4 rounded-xl border border-white/10 backdrop-blur-sm shadow-xl">
                <p className="text-white font-medium">Our Vision</p>
                <p className="text-sm text-gray-300">Empowering digital innovation</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-8">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-blue-950">Our Mission</h2>
              <p className="text-navy-900 leading-relaxed">
                We bridge the gap between creativity and technology, offering expertly crafted templates that save time and enhance productivity. Our goal is to make professional-grade web design achievable for everyone.
              </p>
              <p className="text-navy-700 leading-relaxed">
                Each template is built with precision, ensuring that developers and businesses can deliver exceptional user experiences effortlessly.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Globe className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-950 font-medium">Global Reach</span>
                </div>
                <p className="text-blue-950 text-sm">Serving clients worldwide</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Heart className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-950 font-medium">Customer First</span>
                </div>
                <p className="text-blue-950 text-sm">Dedicated to your success</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl font-bold text-blue-950">Why Choose TemplateHub?</h2>
          <p className="text-navy-900 max-w-2xl mx-auto">
            We combine cutting-edge technology with exceptional design to deliver templates that exceed expectations.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              Icon: Shield,
              title: "Quality Guaranteed",
              description: "Rigorously tested templates ensuring exceptional quality and reliability.",
            },
            {
              Icon: Users,
              title: "Expert Support",
              description: "Dedicated support team ensuring your success every step of the way.",
            },
            {
              Icon: Code,
              title: "Modern Tech Stack",
              description: "Built with cutting-edge technologies for scalable, future-proof solutions.",
            },
          ].map(({ Icon, title, description }, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/10 hover:border-white/20 transition-all"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
              <div className="relative space-y-4">
                <Icon className="w-10 h-10 text-navy-900" />
                <h3 className="text-xl font-semibold text-navy-900">{title}</h3>
                <p className="text-navy-900">{description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team Section */}
      <div className="max-w-7xl mx-auto px-6 py-24 border-t border-white/10">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl font-bold text-blue-950">Meet Our Team</h2>
          <p className="text-navy-900 max-w-2xl mx-auto">
            The talented individuals behind TemplateHub's success.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-12">
          {[
            {
              name: "Tom Wilson",
              role: "Lead Developer",
              image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e",
            },
            {
              name: "Sarah Johnson",
              role: "UI/UX Designer",
              image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80",
            },
            {
              name: "Mike Brown",
              role: "Support Lead",
              image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e",
            },
          ].map(({ name, role, image }, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm rounded-xl border border-white/10 p-8 text-center"
            >
              <div className="space-y-4">
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all"></div>
                  <img
                    src={image}
                    alt={name}
                    className="relative w-32 h-32 rounded-full object-cover border-2 border-white/20 group-hover:border-white/40 transition-all"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-navy-900">{name}</h3>
                  <p className="text-navy-900">{role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}